import { useSetAtom } from "jotai";
import { useRouter } from "@tanstack/react-router";
import { proDialog, registerDialog } from "@/state/dialogs";
import { getResourceInfo } from "@/utilities/getResourceInfo";
import { useMobile } from "@/providers/mobileProvider";
import type { SchemaNames } from "instantdb/types";
import type { Resource, ResourceValues } from "@/lib/types";
import { useUser } from "@/hooks/useUser";

type ResourceAtoms = {
  slug: SchemaNames;
  canCreate?: boolean;
};

type ResourceAtomsReturn = {
  handleCreateResource: ({
    healthAreaId,
    dateTime,
  }: { healthAreaId?: string; dateTime?: Date }) => void;
  handleCopyResource: (resource: Omit<Resource, "id">) => void;
  handleEditResource: (resource: Resource) => void;
};

// User can create a healthArea/goal/routine from scratch or from the atom
// User can copy a healthArea/goal/routine from library
export const useResourceAtoms = ({ slug, canCreate }: ResourceAtoms): ResourceAtomsReturn => {
  const router = useRouter();
  const { isMobile } = useMobile();
  const { atom, pathManage } = getResourceInfo(slug);
  const { isAuthenticated } = useUser();
  const setManagedAtom = useSetAtom(atom);
  const setProDialogOpen = useSetAtom(proDialog);
  const setRegisterDialogOpen = useSetAtom(registerDialog);

  const handleCreateResource = ({
    healthAreaId,
    dateTime,
  }: { healthAreaId?: string; dateTime?: Date }) => {
    if (!isAuthenticated) {
      return setRegisterDialogOpen(true);
    }
    if (!canCreate) {
      return setProDialogOpen(true);
    }

    setManagedAtom({
      mode: "create",
      openSheet: !isMobile,
      healthAreaId,
      dateTime,
    });

    if (isMobile) {
      router.navigate({ to: pathManage });
    }
  };

  const handleCopyResource = (resource: Omit<Resource, "id">) => {
    if (!isAuthenticated) {
      return setRegisterDialogOpen(true);
    }
    if (!canCreate) {
      return setProDialogOpen(true);
    }

    setManagedAtom({
      mode: "create",
      openSheet: !isMobile,
      formData: resource as unknown as ResourceValues,
    });

    if (isMobile) {
      router.navigate({ to: pathManage });
    }
  };

  const handleEditResource = (resource: Resource) => {
    setManagedAtom({
      mode: "edit",
      openSheet: !isMobile,
      resource,
    });

    if (isMobile) {
      router.navigate({ to: pathManage });
    }
  };

  return {
    handleCreateResource,
    handleCopyResource,
    handleEditResource,
  };
};
